// What the line editor is holding, kept by the till rather than the sheet.
//
// The sheet can close and open again, and closing never discards what was
// typed: a cashier who taps outside the editor and back finds their answers
// where they left them. So the answers live here, in the till's state, and
// the sheet only shows them.
//
// The checks are a courtesy, not the rule: the server runs the same ones on
// the quote and again at checkout.

import type { components } from '../api/types';
import {
	MAX_GRAMS,
	MIN_GRAMS,
	perKgChoiceMinor,
	recomputeTotals,
	type LineModifier,
} from '../domain/pricing';
import { weighedBase, type DraftLine } from '../draft/draft.svelte';

type Menu = components['schemas']['MenuBody'];
type Group = components['schemas']['GroupBody'];

export interface EditorWork {
	/** The cart line being changed, or empty for a line not in the cart yet. */
	key: string;
	itemId: string;
	name: string;
	unitBaseMinor: number;
	/** Chosen modifier ids, across every option. */
	chosen: string[];
	qty: number;
	/** The next quantity key replaces the number rather than extending it. */
	qtyFresh: boolean;
	note: string;
	/**
	 * A weighed portion's kilo price and the whole grams typed so far (#917),
	 * 0 before the first digit; absent for an item sold by the piece. Such a
	 * line is one portion: its qty stays 1.
	 */
	perKgMinor?: number;
	grams?: number;
}

/** True for a portion of an item sold by weight. */
export function weighed(work: EditorWork): boolean {
	return work.perKgMinor !== undefined;
}

/** Start editing a line already in the cart. */
export function workFromLine(line: DraftLine): EditorWork {
	return {
		key: line.key,
		itemId: line.itemId,
		name: line.name,
		unitBaseMinor: line.unitBaseMinor,
		chosen: line.modifiers.map((m) => m.id),
		qty: line.qty,
		qtyFresh: true,
		note: line.note,
		...(line.weight ? { perKgMinor: line.weight.perKgMinor, grams: line.weight.grams } : {}),
	};
}

/** Start a line that offers its options before it reaches the cart. */
export function workForNew(itemId: string, name: string, unitBaseMinor: number): EditorWork {
	return { key: '', itemId, name, unitBaseMinor, chosen: [], qty: 1, qtyFresh: true, note: '' };
}

/**
 * Start a portion of an item sold by weight (#917): its grams are asked
 * first, so they start empty, and its price is the kilo price.
 */
export function workForWeighed(itemId: string, name: string, perKgMinor: number): EditorWork {
	return { ...workForNew(itemId, name, 0), perKgMinor, grams: 0 };
}

/** The grams a weighed portion may be sold at: whole, MIN_GRAMS..MAX_GRAMS. */
export function gramsOk(work: EditorWork): boolean {
	const grams = work.grams ?? 0;
	return Number.isInteger(grams) && grams >= MIN_GRAMS && grams <= MAX_GRAMS;
}

/** The base a line starts from: a weighed portion's grams at its kilo price. */
export function baseOf(work: EditorWork): number {
	if (work.perKgMinor === undefined) return work.unitBaseMinor;
	return gramsOk(work) ? weighedBase({ grams: work.grams ?? 0, perKgMinor: work.perKgMinor }) : 0;
}

/** True for a group whose choices are priced per kilogram (UC-3.R11). */
export function perKgGroup(group: { kind?: string | null }): boolean {
	return group.kind === 'perKg';
}

/**
 * The options an item offers, in its order. A per-kg group belongs only on an
 * item sold by weight (UC-10.R11), so a piece item never shows one.
 */
export function groupsOf(menu: Menu, itemId: string): Group[] {
	const item = (menu.items ?? []).find((i) => i.id === itemId);
	const byWeight = item?.soldBy === 'weight';
	return (item?.groups ?? [])
		.map((id) => (menu.groups ?? []).find((g) => g.id === id))
		.filter((g): g is Group => g !== undefined && (byWeight || !perKgGroup(g)));
}

/** True when the item has an option that must be chosen. */
export function asksFirst(menu: Menu, itemId: string): boolean {
	return groupsOf(menu, itemId).some((g) => g.minSelect >= 1);
}

/**
 * What a per-kg choice adds to a portion of grams (UC-3.R11), through the
 * pricing mirror's rule; 0 before the portion has grams it may be sold at.
 */
export function perKgAmount(perKgMinor: number, grams: number | undefined): number {
	const g = grams ?? 0;
	return Number.isInteger(g) && g >= MIN_GRAMS && g <= MAX_GRAMS
		? perKgChoiceMinor(perKgMinor, g)
		: 0;
}

/**
 * The chosen choices as a line keeps them. A per-kg choice keeps its kind,
 * its rate and, as priceDeltaMinor, its amount for the portion's grams, so an
 * older bundle that adds priceDeltaMinor prices the line alike.
 */
export function pickedModifiers(menu: Menu, chosen: string[], grams?: number): LineModifier[] {
	const out: LineModifier[] = [];
	for (const id of chosen) {
		for (const group of menu.groups ?? []) {
			const found = (group.modifiers ?? []).find((m) => m.id === id);
			if (found) {
				out.push(
					perKgGroup(group)
						? {
								id: found.id,
								name: found.name,
								priceDeltaMinor: perKgAmount(found.priceDeltaMinor, grams),
								kind: 'perKg',
								perKgMinor: found.priceDeltaMinor,
							}
						: { id: found.id, name: found.name, priceDeltaMinor: found.priceDeltaMinor },
				);
				break;
			}
		}
	}
	return out;
}

export function unitOf(menu: Menu, work: EditorWork): number {
	return pickedModifiers(menu, work.chosen, work.grams).reduce(
		(sum, m) => sum + m.priceDeltaMinor,
		baseOf(work),
	);
}

/**
 * Whether the pricing mirror takes the line as the editor holds it: a
 * weighed portion whose unit or a per-kg amount passes MAX_MINOR is refused,
 * as the server refuses it (UC-3.R10).
 */
function priced(menu: Menu, work: EditorWork): boolean {
	if (!weighed(work)) return true;
	try {
		recomputeTotals(
			[
				{
					itemId: work.itemId,
					name: work.name,
					unitBaseMinor: baseOf(work),
					modifiers: pickedModifiers(menu, work.chosen, work.grams),
					qty: 1,
					weight: { grams: work.grams ?? 0, perKgMinor: work.perKgMinor ?? 0 },
				},
			],
			0,
			null,
			0,
		);
		return true;
	} catch {
		return false;
	}
}

/**
 * Done waits for every required answer and a unit price not below zero, and a
 * weighed portion for its grams and a price the server takes.
 */
export function blocked(menu: Menu, work: EditorWork): boolean {
	const short = groupsOf(menu, work.itemId).some((group) => {
		const ids = (group.modifiers ?? []).map((m) => m.id);
		return work.chosen.filter((id) => ids.includes(id)).length < group.minSelect;
	});
	return (
		short || unitOf(menu, work) < 0 || (weighed(work) && !gramsOk(work)) || !priced(menu, work)
	);
}
